import { Link } from "react-router-dom";
import { ChevronRight, Shield, Wrench, Sparkles, TrendingUp, MapPin, Award } from "lucide-react";
import { Button } from "../components/ui/button";
import { SEO, LocalBusinessSchema } from "../components/SEO";
import { productGroups, products, referenceImages } from "../data/content";

const HomePage = () => {
  return (
    <div data-testid="home-page">
      <SEO path="/" />
      <LocalBusinessSchema />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('/images/hero.webp')` 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/85 via-stone-900/70 to-stone-900/40" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-16 sm:py-24">
          <div className="max-w-2xl">
            <div className="w-16 h-1 bg-amber-500 mb-6 sm:mb-8" />
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-4 sm:mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Időtálló alumínium teraszmegoldások minden otthonhoz
            </h1>
            <p className="text-base sm:text-lg text-stone-200 mb-8 sm:mb-10 max-w-xl">
              Egyedi tervezésű, időtálló alumínium szerkezetek <strong className="text-white">Magyarország teljes területén</strong>. Teraszok, teraszfedések, ZIP screen árnyékolók és kocsibeállók. <strong className="text-amber-400">5 év garanciával.</strong>
            </p>
            <p className="text-amber-400 font-medium mb-6 text-sm sm:text-base">
              Kérjen ingyenes árajánlatot!
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                asChild
                className="rounded-none px-8 sm:px-10 py-5 sm:py-6 bg-amber-600 text-white hover:bg-amber-700 font-medium text-sm tracking-wider uppercase"
              >
                <Link to="/arajanlat-keres">
                  Ajánlatot kérek
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="rounded-none px-8 sm:px-10 py-5 sm:py-6 border-white/30 text-white hover:bg-white/10 font-medium text-sm tracking-wider uppercase"
              >
                <Link to="/termekek">Termékeink</Link>
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600" />
      </section>

      {/* Products Preview Section */}
      <section className="py-16 sm:py-24 bg-white" data-testid="products-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="w-12 h-1 bg-amber-500 mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Termékeink
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Fedezze fel prémium alumínium szerkezeteink választékát
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {productGroups.map((group) => {
              const subcatIds = group.subcategories.length > 0 ? group.subcategories : [group.id];
              return subcatIds.map((catId) => {
                const product = products[catId];
                if (!product) return null;
                return (
                  <Link 
                    key={catId} 
                    to={`/termekek/${catId}`}
                    className="group block"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img 
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <p className="text-amber-400 text-xs font-medium mb-1">{product.groupName}</p>
                        <h3 className="text-sm sm:text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{product.name}</h3>
                      </div>
                    </div>
                  </Link>
                );
              });
            })}
          </div>

          <div className="text-center mt-10">
            <Button asChild variant="outline" className="rounded-none px-8 py-4">
              <Link to="/termekek">Összes termék megtekintése</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-16 sm:py-24 bg-slate-800 text-white" data-testid="why-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="w-12 h-1 bg-amber-500 mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Miért minket válasszon?
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-6 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-amber-500 flex items-center justify-center">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>5 év garancia</h3>
              <p className="text-slate-400 text-sm">Minden munkánkra</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-amber-500 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Tartósság</h3>
              <p className="text-slate-400 text-sm">Időjárásálló, nem korhad</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-amber-500 flex items-center justify-center">
                <Wrench className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Karbantartásmentes</h3>
              <p className="text-slate-400 text-sm">Nincs festés, csiszolás</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-amber-500 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Modern design</h3>
              <p className="text-slate-400 text-sm">Letisztult formavilág</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-amber-500 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Értéknövelő</h3>
              <p className="text-slate-400 text-sm">Növeli ingatlan értékét</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-amber-500 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Országos</h3>
              <p className="text-slate-400 text-sm">Egész Magyarországon</p>
            </div>
          </div>
        </div>
      </section>

      {/* References Preview - show first image of each group */}
      <section className="py-16 sm:py-24 bg-stone-100" data-testid="references-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="w-12 h-1 bg-amber-500 mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Referenciáink
            </h2>
            <p className="text-slate-600 mt-4">Tekintse meg elkészült projektjeinket</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {productGroups.map((group) => {
              const firstImage = referenceImages[group.id]?.[0];
              if (!firstImage) return null;
              return (
                <Link 
                  key={group.id} 
                  to={`/referenciak?filter=${group.id}`}
                  className="group"
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img 
                      src={firstImage.src}
                      alt={group.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <p className="text-white text-sm font-medium">{group.name}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Button asChild variant="outline" className="rounded-none px-8 py-4">
              <Link to="/referenciak">Összes referencia</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      <section className="py-16 sm:py-24 bg-white" data-testid="blog-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="w-12 h-1 bg-amber-500 mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Blog
            </h2>
            <p className="text-slate-600 mt-4">Hasznos tippek és inspirációk</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/blog/aluminium-vagy-fa-terasz" className="group">
              <div className="aspect-[16/10] overflow-hidden mb-4">
                <img 
                  src="/images/aluminium-teraszok/aluminium-teraszok-4.webp"
                  alt="Alumínium vs fa"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="font-semibold text-slate-900 group-hover:text-amber-600 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Alumínium vagy fa terasz?
              </h3>
              <p className="text-slate-600 text-sm mt-2">Melyiket válasszam? Összehasonlítás.</p>
            </Link>

            <Link to="/blog/uveg-vagy-polikarbonat-teraszfedes" className="group">
              <div className="aspect-[16/10] overflow-hidden mb-4">
                <img 
                  src="/images/teraszfedesek/teraszfedes-9.webp"
                  alt="Üveg vs polikarbonát"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="font-semibold text-slate-900 group-hover:text-amber-600 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Üveg vagy polikarbonát teraszfedés?
              </h3>
              <p className="text-slate-600 text-sm mt-2">Mindkét megoldás előnyei és hátrányai.</p>
            </Link>

            <Link to="/blog/zip-screen-arnyekolok-elonyei" className="group">
              <div className="aspect-[16/10] overflow-hidden mb-4">
                <img 
                  src="/images/arnyekolok/ref-arnyekolo-3.webp"
                  alt="ZIP screen"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="font-semibold text-slate-900 group-hover:text-amber-600 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                ZIP screen árnyékolók előnyei
              </h3>
              <p className="text-slate-600 text-sm mt-2">A modern árnyékolás legjava.</p>
            </Link>
          </div>

          <div className="text-center mt-10">
            <Button asChild variant="outline" className="rounded-none px-8 py-4">
              <Link to="/blog">Összes bejegyzés</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 sm:py-24 bg-amber-600" data-testid="contact-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Kérjen ingyenes árajánlatot!
          </h2>
          <p className="text-amber-100 mb-8">
            Vegye fel velünk a kapcsolatot és segítünk megtalálni az Ön igényeinek megfelelő megoldást.
          </p>
          <Button 
            asChild
            className="rounded-none px-10 py-6 bg-white text-amber-600 hover:bg-stone-100 font-medium text-sm tracking-wider uppercase"
          >
            <Link to="/arajanlat-keres">Ajánlatot kérek</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
